import React, { useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  LongTextLabel,
} from "@sscs/ui";
import { NOTIFY_ON_COMPLETION_LABEL } from "Constants";

type DialogProps = {
  fileName: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (notifyOnCompletion: boolean) => Promise<void>;
};

const ReanalyzeDialogContent = ({
  fileName,
  onClose,
  onConfirm,
}: Omit<DialogProps, "isOpen">): React.JSX.Element => {
  const [notifyOnCompletion, setNotifyOnCompletion] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      await onConfirm(notifyOnCompletion);
    } finally {
      setIsSubmitting(false);
      onClose();
    }
  };

  return (
    <>
      <Dialog.Header>
        <Dialog.Header.Title>Reanalyze</Dialog.Header.Title>
      </Dialog.Header>
      <Dialog.Body>
        <Dialog.Section>
          <Dialog.Section.Description>
            You&#39;re about to reanalyze{" "}
            <b>
              <LongTextLabel title={fileName} label={fileName} inline />
            </b>
            . This will put the file(s) in the analysis queue.
          </Dialog.Section.Description>
          <Dialog.Section.Content>
            <FormControlLabel
              label={NOTIFY_ON_COMPLETION_LABEL}
              control={
                <Checkbox
                  checked={notifyOnCompletion}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setNotifyOnCompletion(event.target.checked)
                  }
                />
              }
            />
          </Dialog.Section.Content>
        </Dialog.Section>
      </Dialog.Body>
      <Dialog.Footer>
        <Dialog.Footer.Actions>
          <Button color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            Reanalyze
          </Button>
        </Dialog.Footer.Actions>
      </Dialog.Footer>
    </>
  );
};

const ReanalyzeDialog = ({
  isOpen,
  ...props
}: DialogProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <ReanalyzeDialogContent {...props} />
  </Dialog.Frame>
);

export default ReanalyzeDialog;
